import { Col, Input, Label, Row } from "reactstrap";
import Select from "react-select";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import SidebarLayout from "../../../../layouts/SidebarLayout";
import PageSubHeader from "../../../../components/Layouts/PageSubHeader";
import sidebar from "../../utils/sidebar";
import FixedSave from "../../../../components/Actions/FixedSave";
import SelectCustomStyles from "../../../../utils/styles/reactSelect";
import { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { get, post } from "../../../../utils/helpers/api";
import { toast } from "react-toastify";
import { format } from "date-fns";
import DefaultButton from "../../../../components/Buttons/Default";
import AddParticipantModal from "./Modals/AddParticipant";
import { AWS_S3_URL } from "../../../../utils/constants";
import { useTranslation } from "react-i18next";
import { useSettings } from "../../../../context/settings";

function CreateMeetingPage() {
  const { t } = useTranslation();
  const { datePickerFormat, timePickerFormat } = useSettings();
  const { state } = useLocation();
  const { id } = useParams();
  const navigate = useNavigate();
  const [initLoading, setInitLoading] = useState(true);
  const [saveLoading, setSaveLoading] = useState(false);
  const [participantModalOpen, setParticipantModalOpen] = useState(false);
  const [customersData, setCustomersData] = useState([]);
  const [employeesData, setEmployeesData] = useState([]);
  const [leadsData, setLeadsData] = useState([]);
  const [meetingData, setMeetingData] = useState({
    title: state?.meetingData?.title || '',
    location: state?.meetingData?.location || '',
    time_from: state?.meetingData?.time_from ? new Date(state.meetingData.time_from) : null,
    time_to: state?.meetingData?.time_to ? new Date(state.meetingData.time_to) : null,
    host_id: state?.meetingData?.host_id || null,
    participants: (state?.meetingData?.participants || []).map(p =>
      p.participant_id ? { id: p.participant_id, type: p.participant_type } : p
    ),
    related_to: state?.meetingData?.related_to || 'customer',
    customer_id: state?.meetingData?.customer_id || null,
    lead_id: state?.meetingData?.lead_id || null,
    description: state?.meetingData?.description || ''
  });

  useEffect(() => {
    initData();
  }, []);

  const initData = async () => {
    setInitLoading(true);
    const data = await get('/crm/meetings/create');
    if (data.success) {
      setCustomersData(data.customers);
      setEmployeesData(data.employees);
      setLeadsData(data.leads);
      setInitLoading(false);
    }
  };

  const saveMeeting = async () => {
    setSaveLoading(true);
    const url = id ? '/crm/meetings/update/' + id : '/crm/meetings/store';
    const payload = {
      ...meetingData,
      time_from: meetingData.time_from ? format(meetingData.time_from, "yyyy-MM-dd HH:mm:ss") : '',
      time_to: meetingData.time_to ? format(meetingData.time_to, "yyyy-MM-dd HH:mm:ss") : '',
    };
    const data = await post(url, payload);
    if (data.success) {
      navigate('/crm/meetings/view/' + data.meeting.id, { state: { meetingData: data.meeting } });
    } else {
      toast.error(data.message || t('common.somethingWentWrong'));
    }
    setSaveLoading(false);
  };

  return (
    <SidebarLayout sidebar={sidebar}>
      <div className="page-with-subheader">
        <PageSubHeader
          breadcrumb={[t('crm.meetings'), id ? t('crm.editMeeting') : t('crm.newMeeting')]}
          onBack={() => navigate('/crm/meetings')}
        />

        <div className="subheader-page-content scrollable">
          <Row>
            <Col xs="6" className="mb-3">
              <Label>{t('contact.title')}: </Label>
              <Input type="text" value={meetingData.title} onChange={(e) => setMeetingData({ ...meetingData, title: e.target.value })} />
            </Col>
            <Col xs="6" className="mb-3">
              <Label>{t('common.location')}: </Label>
              <Input type="select" value={meetingData.location} onChange={(e) => setMeetingData({ ...meetingData, location: e.target.value })}>
                <option value="">{t('common.selectLocation')}</option>
                <option value="in_office">{t('common.inOffice')}</option>
                <option value="client_office">{t('sales.clientOffice')}</option>
                <option value="online">{t('common.online')}</option>
              </Input>
            </Col>
            <Col xs="6" className="mb-3">
              <Label>{t('common.from')}: </Label>
              <DatePicker
                selected={meetingData.time_from}
                onChange={(d) => setMeetingData({ ...meetingData, time_from: d })}
                showTimeSelect
                timeFormat={timePickerFormat}
                timeIntervals={15}
                dateFormat={`${datePickerFormat} ${timePickerFormat}`}
                className="form-control"
                placeholderText={t('crm.selectDateTime')}
              />
            </Col>
            <Col xs="6" className="mb-3">
              <Label>{t('common.to')}: </Label>
              <DatePicker
                selected={meetingData.time_to}
                onChange={(d) => setMeetingData({ ...meetingData, time_to: d })}
                showTimeSelect
                timeFormat={timePickerFormat}
                timeIntervals={15}
                dateFormat={`${datePickerFormat} ${timePickerFormat}`}
                className="form-control"
                placeholderText={t('crm.selectDateTime')}
                minDate={meetingData.time_from}
              />
            </Col>
            <Col xs="6" className="mb-3">
              <Label>{t('crm.host')} <span className="text-danger">*</span></Label>
              <Select
                options={employeesData.map(e => ({ value: e.id, label: e.firstname + ' ' + e.lastname }))}
                styles={SelectCustomStyles}
                value={employeesData.map(e => ({ value: e.id, label: e.firstname + ' ' + e.lastname })).find(o => o.value === meetingData.host_id) || null}
                onChange={selected => setMeetingData({ ...meetingData, host_id: selected ? selected.value : null })}
              />
            </Col>
            <Col xs="6" className="mb-3">
              <Label>{t('crm.participants')} </Label>
              <div className="d-flex align-items-center gap-2 flex-wrap">
                <DefaultButton outline size="sm" onClick={() => setParticipantModalOpen(true)}>
                  + {t('crm.addParticipant')}
                </DefaultButton>
                {meetingData.participants.map(participant => {
                  const list = participant.type === 'employee' ? employeesData : customersData;
                  const person = list.find(x => x.id === participant.id);
                  if (!person) return null;
                  return (
                    <div key={`${participant.type}-${participant.id}`} className="d-flex me-2 align-items-center gap-1">
                      <img
                        src={person?.profile_picture_path ? AWS_S3_URL + person.profile_picture_path : 'https://placehold.co/40'}
                        alt={person.firstname}
                        style={{ width: 28, height: 28, borderRadius: '50%', objectFit: 'cover' }}
                        className="border"
                      />
                      <span className="small">
                        {person?.company_name || `${person.firstname} ${person.lastname}`}
                      </span>
                    </div>
                  );
                })}
              </div>
            </Col>
            <Col xs="6" className="mb-3">
              <Label>{t('crm.relatedTo')} </Label>
              <Row className="g-1">
                <Col xs="4">
                  <Input type="select" value={meetingData.related_to} onChange={e => setMeetingData({ ...meetingData, related_to: e.target.value, customer_id: null, lead_id: null })}>
                    <option value="customer">{t('crm.customer')}</option>
                    <option value="lead">{t('crm.lead')}</option>
                  </Input>
                </Col>
                <Col xs="8">
                  {meetingData.related_to === 'customer' ? (
                    <Select
                      options={customersData.map(c => ({ value: c.id, label: c.company_name ?? c.firstname + ' ' + c.lastname }))}
                      styles={SelectCustomStyles}
                      value={customersData.map(c => ({ value: c.id, label: c.company_name ?? c.firstname + ' ' + c.lastname })).find(o => o.value === meetingData.customer_id) || null}
                      onChange={selected => setMeetingData({ ...meetingData, customer_id: selected ? selected.value : null })}
                    />
                  ) : (
                    <Select
                      options={leadsData.map(l => ({ value: l.id, label: l.name ?? l.firstname + ' ' + l.lastname }))}
                      styles={SelectCustomStyles}
                      value={leadsData.map(l => ({ value: l.id, label: l.name ?? l.firstname + ' ' + l.lastname })).find(o => o.value === meetingData.lead_id) || null}
                      onChange={selected => setMeetingData({ ...meetingData, lead_id: selected ? selected.value : null })}
                    />
                  )}
                </Col>
              </Row>
            </Col>
            <Col md="12" className="mb-3">
              <Label>{t('common.description')}: </Label>
              <Input type="textarea" rows="4" value={meetingData.description} onChange={(e) => setMeetingData({ ...meetingData, description: e.target.value })} />
            </Col>
          </Row>
        </div>
      </div>

      <AddParticipantModal
        isOpen={participantModalOpen}
        toggle={() => setParticipantModalOpen(false)}
        employeesData={employeesData}
        customersData={customersData}
        participants={meetingData.participants}
        onSave={(participants) => setMeetingData(prev => ({ ...prev, participants }))}
      />

      <FixedSave 
        onSave={saveMeeting} 
        loading={saveLoading} 
        disabled={initLoading || saveLoading || !meetingData.host_id} 
      />
    </SidebarLayout>
  );
}

export default CreateMeetingPage;
